import { FC, PropsWithChildren, useState } from "react"
import { Button, Form, Modal } from "react-bootstrap"
import { unstable_batchedUpdates } from "react-dom"
import { useTranslation } from "react-i18next"
import Select from "react-select/creatable"
import api from "../../api"
import { useAppSelector } from "../../app/hooks"
import { RootState } from "../../store"

type Props = {
    show: string
    setShow: (...args: any) => any
    showMessage: (message: string, variant: string, title: string) => void
    mawb?: any
    attachment?: {
        id: number
        file_name: string
    }
}

const generateBase64 = (file: File) =>
    new Promise<string>((resolve) => {
        const reader = new FileReader()
        reader.onload = (fileLoadedEvent) => {
            resolve(`${fileLoadedEvent.target?.result ?? ""}`)
        }
        reader.readAsDataURL(file)
    })

const emptyArray: string[] = []

const UploadModal: FC<PropsWithChildren<Props>> = (props) => {
    const { t } = useTranslation()
    const { list, loading, error } = useAppSelector((state: RootState) => state.linehaul.attachments)
    const types = list.map((label, id) => ({ id, label }))

    const [formValue, setFormValue] = useState<{
        type: { id: number; label: string } | null
        file: string
        fileName: string
        description: ""
    }>({ type: null, file: "", fileName: "", description: "" })

    const [errors, setErrors] = useState<string[]>([])
    const [submitting, setSubmitting] = useState<boolean>(false)

    const getErrors = <T extends Partial<typeof formValue>>(value: T) => {
        const errors = Object.keys(value)
            .filter((key) => !["description"].includes(key))
            .map((key) => (!(value as any)[key] ? key : null))
            .filter((key) => !!key) as string[]
        return errors
    }

    const handleChange = (e: any) => {
        const key = e.target?.name
        const value = e.target?.value ?? null
        if (key) {
            setErrors(emptyArray)
            setFormValue((oldFormValue) => ({
                ...oldFormValue,
                [key]: value
            }))
        }
    }

    const handleFileSelection = async (e: any) => {
        const file = e.target?.files?.[0]
        if (!file) {
            unstable_batchedUpdates(() => {
                handleChange({ target: { name: "file", value: "" } })
                handleChange({ target: { name: "fileName", value: "" } })
            })
            return
        }
        const base64 = await generateBase64(file)
        unstable_batchedUpdates(() => {
            handleChange({ target: { name: "file", value: base64 } })
            handleChange({ target: { name: "fileName", value: file.name } })
        })
    }

    const handleSave = async (e: any) => {
        e.preventDefault()
        const { file, type } = formValue
        let errors = getErrors({ file, type })
        if (errors.length) {
            setErrors(errors)
            return
        }
        try {
            setSubmitting(true)
            const res = await api.linehaul.uploadMAWBAttachment({
                document_name: formValue.type?.label ?? "",
                file_name: formValue.fileName,
                file: formValue.file,
                description: formValue.description,
                mawbId: props.mawb?.id
            })
            if (!res.status) throw new Error("Unable to upload attachment")
            setSubmitting(false)
            props.showMessage(res?.message ?? "Attachment uploaded successfully", "success", "Success")
        } catch (error: any) {
            setSubmitting(false)
            props.showMessage(`Unable to upload attachment, reason: ${error.message}`, "danger", "Error")
        }
    }

    const handleDelete = async (e: any) => {
        e.preventDefault()
        try {
            setSubmitting(true)
            const res = await api.linehaul.deleteMAWBAttachment({
                attachmentId: props.attachment?.id ?? 0
            })
            if (!res.status) throw new Error("Unable to upload attachment")
            setSubmitting(false)
            props.showMessage(res?.message ?? "Attachment deleted successfully", "success", "Success")
        } catch (error: any) {
            props.showMessage(`Unable to delete attachment, reason: ${error.message}`, "danger", "Error")
        }
    }

    const handleSubmit =
        props.show === "documentDelete"
            ? handleDelete
            : props.show === "documentUpload"
              ? handleSave
              : (e: any) => {}

    const handleClose = () => {
        props.setShow("")
    }

    const disabled = submitting || loading

    return (
        <Modal
            size="lg"
            show={["documentDelete", "documentUpload"].includes(props.show)}
            onHide={handleClose}
            backdrop="static"
            scrollable
            keyboard={false}>
            <Modal.Header closeButton={!disabled}>
                <Modal.Title className="text-uppercase">
                    {props.show === "documentDelete"
                        ? "Delete"
                        : props.show === "documentUpload"
                          ? "Upload"
                          : ""}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {error ? (
                    <p className="text-danger">Unable to load attachment types</p>
                ) : (
                    <Form noValidate onSubmit={handleSubmit}>
                        {props.show === "documentDelete" ? (
                            // Delete
                            <div>
                                Are you sure you want to Delete document{" "}
                                <strong>{props.attachment?.file_name ?? ""}?</strong>
                            </div>
                        ) : props.show === "documentUpload" ? (
                            // Upload
                            <div>
                                <div className="mb-3">
                                    <Form.Group className="w-100">
                                        <Form.Label htmlFor="type" className="required">
                                            {t("Type")}
                                        </Form.Label>
                                        <Select
                                            className={`${errors.includes("type") ? "is-invalid" : ""}`}
                                            isSearchable
                                            isClearable
                                            placeholder="Type"
                                            options={types}
                                            styles={{
                                                control: (baseStyles, state) => ({
                                                    ...baseStyles,
                                                    ...(errors.includes("type") ? { borderColor: "red" } : {})
                                                }),
                                                option: (baseStyles, state) => ({
                                                    ...baseStyles,
                                                    backgroundColor: "white",
                                                    color: "black"
                                                })
                                            }}
                                            value={formValue.type}
                                            onChange={(value) => {
                                                handleChange({
                                                    target: {
                                                        name: "type",
                                                        value: value ?? null
                                                    }
                                                })
                                            }}
                                        />
                                        <Form.Control.Feedback type="invalid">
                                            {t(`This field is required`)}
                                        </Form.Control.Feedback>
                                    </Form.Group>
                                </div>
                                <div className="mb-3">
                                    <Form.Group className="w-100">
                                        <Form.Label htmlFor="file" className="required">
                                            {t("File")}
                                        </Form.Label>
                                        <Form.Control
                                            type="file"
                                            name="file"
                                            onChange={handleFileSelection}
                                            placeholder="File"
                                            isInvalid={errors.includes("file")}
                                        />
                                        <Form.Control.Feedback type="invalid">
                                            {t(`This field is required`)}
                                        </Form.Control.Feedback>
                                    </Form.Group>
                                </div>
                                <div className="mb-3">
                                    <Form.Group className="w-100">
                                        <Form.Label htmlFor="description">{t("Description")}</Form.Label>
                                        <Form.Control
                                            value={formValue.description}
                                            type="text"
                                            name="description"
                                            onChange={handleChange}
                                            placeholder="Description"
                                        />
                                    </Form.Group>
                                </div>
                            </div>
                        ) : (
                            <></>
                        )}
                        <div className="d-flex justify-content-end">
                            <Button
                                type="button"
                                disabled={disabled}
                                className="float-end mb-2 gray-bg px-3 mx2"
                                onClick={handleClose}>
                                {t("Cancel")}
                            </Button>
                            <Button
                                disabled={disabled}
                                type="submit"
                                className="float-end mb-2 primary-bg px-3 mx-2">
                                {disabled ? (
                                    <div className="text-center">
                                        <div className="spinner-border spinner-border-sm" role="status">
                                            <span className="sr-only"></span>
                                        </div>
                                    </div>
                                ) : props.show === "documentDelete" ? (
                                    t("Delete")
                                ) : props.show === "documentUpload" ? (
                                    t("Save")
                                ) : null}
                            </Button>
                        </div>
                    </Form>
                )}
            </Modal.Body>
        </Modal>
    )
}

export default UploadModal
